import md5 from 'js-md5';
import { requestTextTranslate } from '@/api/mail';
import {
  getTextTranslationCache,
  getTranslationCache,
  setTextTranslationCache,
  setTranslationCache,
} from '@/mail/cache/translationCache';
import { exportMailHtml, parseMailHtml } from '@/mail/parser/htmlParser';

const SKIP_TRANSLATE_TAGS = new Set(['PRE', 'CODE', 'TEXTAREA', 'SVG', 'CANVAS', 'SCRIPT', 'STYLE', 'NOSCRIPT']);
const DEFAULT_MAX_CONCURRENT_REQUESTS = 1;
const DEFAULT_MAX_REQUESTS_PER_SECOND = 2;
const TRANSLATE_REQUEST_INTERVAL_MS = Math.ceil(1000 / DEFAULT_MAX_REQUESTS_PER_SECOND);
const TRANSLATE_TEXT_API_LIMIT = 6000;
const TRANSLATE_SAFE_MARGIN_CHARACTERS = 200;
const MAX_SINGLE_REQUEST_CHARACTERS = TRANSLATE_TEXT_API_LIMIT - TRANSLATE_SAFE_MARGIN_CHARACTERS;
const MAX_TEXT_UNIT_CHARACTERS = MAX_SINGLE_REQUEST_CHARACTERS;
const MAX_BATCH_PAYLOAD_CHARACTERS = TRANSLATE_TEXT_API_LIMIT - TRANSLATE_SAFE_MARGIN_CHARACTERS;
const BATCH_TEXT_SEPARATOR_TOKEN = '@@MAIL_SPLIT@@';
const BATCH_TEXT_SEPARATOR = `\n${BATCH_TEXT_SEPARATOR_TOKEN}\n`;
const BATCH_TEXT_ESCAPE = '__MAIL_SPLIT_ESC__';
const INLINE_TOKEN_PLACEHOLDER_PREFIX = '__MT';
const INLINE_TOKEN_PLACEHOLDER_SUFFIX = '__';
const BATCH_TEXT_SEPARATOR_REG = /\s*@@MAIL_SPLIT@@\s*/g;
const URL_REG = /^(https?:\/\/|www\.)\S+$/i;
const EMAIL_REG = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const UUID_REG = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PURE_NUMBER_REG = /^[\d\s.,:%+\-/$￥]+$/;
const CSS_DECLARATION_REG = /^([.#]?[a-zA-Z_-][\w-]*\s*\{[\s\S]*\}|[a-zA-Z-]+\s*:\s*[^;]+;?\s*)+$/;
const JS_SNIPPET_REG = /^(const|let|var|function\b|return\b|if\s*\(|for\s*\(|while\s*\(|document\.|window\.|console\.|import\s+|export\s+)/;
const PLACEHOLDER_REG = /^(\{\{[\s\S]*\}\}|\[\[[\s\S]*\]\]|\$\{[\s\S]*\}|__\S+__|%[sdif])$/;
const BASE64_REG = /^(?:[A-Za-z0-9+/]{4}){8,}(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const INLINE_PROTECTED_TOKEN_REG = /(https?:\/\/[^\s<>"')]+|www\.[^\s<>"')]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|\{\{[\s\S]*?\}\}|\[\[[\s\S]*?\]\]|\$\{[\s\S]*?\}|__[\w-]+__|%[sdif])/gi;
let translateRequestSchedule = Promise.resolve();
let nextTranslateRequestTime = 0;
const inFlightTextTranslationRequests = new Map();
const inFlightMailTranslationTasks = new Map();

function wait(duration) {
  return new Promise((resolve) => {
    setTimeout(resolve, Math.max(0, duration));
  });
}

function decodeHtmlEntities(text) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = String(text || '');
  return textarea.value;
}

function normalizeTextForTranslation(text) {
  const decodedText = decodeHtmlEntities(String(text || '').replace(/\u00a0/g, ' '))
    .replace(/\r\n?|\u2028|\u2029/g, '\n');

  return {
    originalText: String(text || ''),
    textToTranslate: decodedText.trim(),
    leadingWhitespace: decodedText.match(/^\s*/)?.[0] || '',
    trailingWhitespace: decodedText.match(/\s*$/)?.[0] || '',
  };
}

function isSpecialToken(text) {
  const trimmedText = String(text || '').trim();
  if (!trimmedText) {
    return true;
  }

  return (
    URL_REG.test(trimmedText) ||
    EMAIL_REG.test(trimmedText) ||
    UUID_REG.test(trimmedText) ||
    PURE_NUMBER_REG.test(trimmedText) ||
    PLACEHOLDER_REG.test(trimmedText) ||
    JS_SNIPPET_REG.test(trimmedText) ||
    (trimmedText.length >= 32 && BASE64_REG.test(trimmedText)) ||
    CSS_DECLARATION_REG.test(trimmedText)
  );
}

function shouldSkipElement(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const hiddenAttr = element.getAttribute('hidden');
  const ariaHiddenAttr = element.getAttribute('aria-hidden');
  const translateAttr = element.getAttribute('translate');
  const inputType = String(element.getAttribute('type') || '').toLowerCase();

  return (
    hiddenAttr !== null ||
    ariaHiddenAttr === 'true' ||
    translateAttr === 'no' ||
    element.hasAttribute('data-no-translate') ||
    inputType === 'hidden'
  );
}

export function shouldTranslateText(text) {
  const trimmedText = String(text || '').trim();
  if (!trimmedText || isSpecialToken(trimmedText)) {
    return false;
  }

  return /[A-Za-z\u3400-\u4dbf\u4e00-\u9fff]/.test(trimmedText);
}

function shouldSkipTextNode(node) {
  if (!node || node.nodeType !== Node.TEXT_NODE) {
    return true;
  }

  let currentElement = node.parentElement;
  while (currentElement) {
    const tagName = String(currentElement.tagName || '').toUpperCase();
    if (SKIP_TRANSLATE_TAGS.has(tagName) || shouldSkipElement(currentElement)) {
      return true;
    }
    currentElement = currentElement.parentElement;
  }

  return false;
}

export function collectTextNodes(root) {
  if (!root) {
    return [];
  }

  const doc = root.ownerDocument || document;
  const walker = doc.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (shouldSkipTextNode(node)) {
          return NodeFilter.FILTER_REJECT;
        }

        const normalized = normalizeTextForTranslation(node.nodeValue || '');
        if (!shouldTranslateText(normalized.textToTranslate)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  const nodes = [];
  let currentNode = walker.nextNode();
  let index = 0;
  while (currentNode) {
    const normalized = normalizeTextForTranslation(currentNode.nodeValue || '');
    nodes.push({
      id: index,
      node: currentNode,
      origin: normalized.originalText,
      textToTranslate: normalized.textToTranslate,
      leadingWhitespace: normalized.leadingWhitespace,
      trailingWhitespace: normalized.trailingWhitespace,
    });
    index += 1;
    currentNode = walker.nextNode();
  }

  return nodes;
}

function findBestSplitIndex(text, maxCharacters) {
  const safeText = String(text || '');
  const hardLimit = Math.min(maxCharacters, safeText.length);
  if (hardLimit <= 0) {
    return 0;
  }

  const minPreferredIndex = Math.max(1, Math.floor(hardLimit * 0.6));
  const searchRange = safeText.slice(0, hardLimit + 1);
  const boundaryRegs = [/\r?\n+/g, /[。！？!?；;]+/g, /[,，:：)）]+/g, /\s+/g];
  let bestIndex = 0;

  boundaryRegs.forEach((reg) => {
    let match;
    while ((match = reg.exec(searchRange)) !== null) {
      const matchEnd = match.index + match[0].length;
      if (matchEnd >= minPreferredIndex && matchEnd <= hardLimit) {
        bestIndex = Math.max(bestIndex, matchEnd);
      }
    }
  });

  return bestIndex || hardLimit;
}

function splitLongText(text, maxCharacters = MAX_TEXT_UNIT_CHARACTERS) {
  const safeText = String(text || '');
  if (!safeText) {
    return [];
  }

  if (safeText.length <= maxCharacters) {
    return [safeText];
  }

  const result = [];
  let remainingText = safeText;
  while (remainingText.length > maxCharacters) {
    const splitIndex = findBestSplitIndex(remainingText, maxCharacters);
    result.push(remainingText.slice(0, splitIndex));
    remainingText = remainingText.slice(splitIndex);
  }

  if (remainingText) {
    result.push(remainingText);
  }

  return result;
}

function estimateBatchPayloadLength(batch) {
  return buildBatchPayload(batch).length;
}

function estimateSingleEntryPayloadLength(text) {
  return String(text || '').length;
}

function splitTextByRequestLimit(text, maxCharacters = MAX_TEXT_UNIT_CHARACTERS) {
  const queue = splitLongText(text, maxCharacters);
  const result = [];

  while (queue.length) {
    const currentSegment = String(queue.shift() || '');
    if (!currentSegment) {
      continue;
    }

    const protectedTokens = protectInlineTokens(currentSegment);
    const payloadLength = estimateSingleEntryPayloadLength(protectedTokens.maskedText);
    if (payloadLength <= MAX_SINGLE_REQUEST_CHARACTERS || currentSegment.length <= 1) {
      result.push({
        sourceText: protectedTokens.maskedText,
        protectedTokens: protectedTokens.replacements,
      });
      continue;
    }

    const preferredSplitLimit = Math.max(
      1,
      Math.min(maxCharacters, Math.floor(currentSegment.length / 2)),
    );
    const splitIndex = findBestSplitIndex(currentSegment, preferredSplitLimit);
    const safeSplitIndex = splitIndex > 0 && splitIndex < currentSegment.length
      ? splitIndex
      : Math.max(1, Math.floor(currentSegment.length / 2));

    queue.unshift(currentSegment.slice(safeSplitIndex));
    queue.unshift(currentSegment.slice(0, safeSplitIndex));
  }

  return result;
}

function protectInlineTokens(text) {
  const replacements = [];
  let tokenIndex = 0;
  const maskedText = String(text || '').replace(INLINE_PROTECTED_TOKEN_REG, (match) => {
    const token = String(match || '');
    if (!token.trim()) {
      return token;
    }

    const placeholder = `${INLINE_TOKEN_PLACEHOLDER_PREFIX}${tokenIndex.toString(36)}${INLINE_TOKEN_PLACEHOLDER_SUFFIX}`;
    replacements.push({
      placeholder,
      value: token,
    });
    tokenIndex += 1;
    return placeholder;
  });

  return {
    maskedText,
    replacements,
  };
}

function restoreInlineTokens(text, protectedTokens = []) {
  return (protectedTokens || []).reduce((result, item) => {
    if (!item?.placeholder) {
      return result;
    }
    return result.split(item.placeholder).join(item.value || '');
  }, String(text || ''));
}

function buildTranslateUnits(textNodes, maxUnitCharacters = MAX_TEXT_UNIT_CHARACTERS) {
  const units = [];

  (textNodes || []).forEach((item) => {
    const segments = splitTextByRequestLimit(item.textToTranslate, maxUnitCharacters);
    segments.forEach((segment, segmentIndex) => {
      const unitId = `${item.id}_${segmentIndex}`;
      units.push({
        nodeId: item.id,
        unitId,
        node: item.node,
        segmentIndex,
        leadingWhitespace: item.leadingWhitespace,
        trailingWhitespace: item.trailingWhitespace,
        sourceText: segment.sourceText,
        protectedTokens: segment.protectedTokens,
      });
    });
  });

  return units;
}

function buildTranslateEntries(units) {
  const safeUnits = Array.isArray(units) ? units : [];
  const entryMap = new Map();

  safeUnits.forEach((unit) => {
    const key = String(unit?.sourceText || '');
    if (!key) {
      return;
    }

    if (!entryMap.has(key)) {
      entryMap.set(key, {
        entryId: `entry_${entryMap.size}`,
        sourceText: key,
        unitIds: [],
      });
    }

    entryMap.get(key).unitIds.push(unit.unitId);
  });

  return Array.from(entryMap.values());
}

function buildTranslateBatches(entries) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  if (!safeEntries.length) {
    return [];
  }

  const sortedEntries = safeEntries
    .slice()
    .sort((left, right) => String(right?.sourceText || '').length - String(left?.sourceText || '').length);
  const batches = [];
  sortedEntries.forEach((entry) => {
    if (!entry?.sourceText) {
      return;
    }

    for (let index = 0; index < batches.length; index += 1) {
      const candidateBatch = [...batches[index], entry];
      if (estimateBatchPayloadLength(candidateBatch) <= MAX_BATCH_PAYLOAD_CHARACTERS) {
        batches[index] = candidateBatch;
        return;
      }
    }

    batches.push([entry]);
  });

  return batches;
}

function buildBatchPayload(batch) {
  const texts = (batch || []).map((item) => escapeBatchSeparator(String(item?.sourceText || '')));
  if (!texts.length) {
    return '';
  }

  return `${BATCH_TEXT_SEPARATOR}${texts.join(BATCH_TEXT_SEPARATOR)}${BATCH_TEXT_SEPARATOR}`;
}

function escapeBatchSeparator(text) {
  return String(text || '')
    .split(BATCH_TEXT_SEPARATOR).join(BATCH_TEXT_ESCAPE)
    .split(BATCH_TEXT_SEPARATOR_TOKEN).join(BATCH_TEXT_ESCAPE);
}

function restoreBatchSeparator(text) {
  return String(text || '').split(BATCH_TEXT_ESCAPE).join(BATCH_TEXT_SEPARATOR_TOKEN);
}

function stripBatchSeparatorArtifacts(text) {
  return String(text || '')
    .split(BATCH_TEXT_ESCAPE).join('')
    .replace(BATCH_TEXT_SEPARATOR_REG, '');
}

function isNonTranslatedSuccessMessage(text) {
  return String(text || '').trim() === '成功';
}

function extractTranslatedText(response, fallbackFields = []) {
  const candidates = [
    response?.message,
    response?.Response?.TargetText,
    response?.TargetText,
    response?.targetText,
    response?.result?.TargetText,
    response?.result?.targetText,
    response?.data?.TargetText,
    response?.data?.targetText,
    response?.data?.result?.TargetText,
    response?.data?.result?.targetText,
    ...fallbackFields,
  ];

  const translatedText = candidates.find((item) => (
    typeof item === 'string'
    && item.trim()
    && !isNonTranslatedSuccessMessage(item)
  ));
  if (!translatedText) {
    throw new Error('未获取到翻译结果');
  }

  return translatedText;
}

function throwTranslateError(response) {
  const responseError = response?.Response?.Error || response?.error || response?.data?.error;
  if (responseError) {
    throw new Error(responseError.Message || responseError.message || '翻译失败');
  }

  if (response?.success === false) {
    throw new Error(response?.message || '翻译失败');
  }

  if (response?.code && Number(response.code) !== 200) {
    throw new Error(response?.message || '翻译失败');
  }
}

async function acquireTranslateRequestSlot() {
  let releaseScheduleLock;
  const previousSchedule = translateRequestSchedule;
  translateRequestSchedule = new Promise((resolve) => {
    releaseScheduleLock = resolve;
  });

  await previousSchedule;
  try {
    const currentTime = Date.now();
    const scheduledTime = Math.max(currentTime, nextTranslateRequestTime);
    const waitTime = scheduledTime - currentTime;
    nextTranslateRequestTime = scheduledTime + TRANSLATE_REQUEST_INTERVAL_MS;

    if (waitTime > 0) {
      await wait(waitTime);
    }
  } finally {
    releaseScheduleLock();
  }
}

async function requestTranslateText(sourceText, source, target) {
  if (String(sourceText || '').length > TRANSLATE_TEXT_API_LIMIT) {
    throw new Error(`翻译文本超过接口上限（${TRANSLATE_TEXT_API_LIMIT} 字符）`);
  }

  const sourceLanguage = source || 'auto';
  const targetLanguage = target || 'en';
  const requestKey = `${sourceLanguage}__${targetLanguage}__${sourceText}`;
  if (inFlightTextTranslationRequests.has(requestKey)) {
    return inFlightTextTranslationRequests.get(requestKey);
  }

  const requestTask = (async () => {
    await acquireTranslateRequestSlot();
    const response = await requestTextTranslate({
      sourceText,
      source: sourceLanguage,
      target: targetLanguage,
    });

    throwTranslateError(response);
    return extractTranslatedText(response);
  })();

  inFlightTextTranslationRequests.set(requestKey, requestTask);
  try {
    return await requestTask;
  } finally {
    inFlightTextTranslationRequests.delete(requestKey);
  }
}

function parseBatchTranslatedText(translatedText, expectedLength) {
  const safeText = String(translatedText || '');
  if (!safeText && expectedLength === 0) {
    return [];
  }

  if (expectedLength === 1) {
    return [stripBatchSeparatorArtifacts(restoreBatchSeparator(safeText))];
  }

  const parts = safeText.split(BATCH_TEXT_SEPARATOR_REG);
  const normalizedParts = parts.filter((item, index) => {
    if (index === 0 || index === parts.length - 1) {
      return String(item || '').trim() !== '';
    }
    return true;
  });

  if (normalizedParts.length === expectedLength) {
    return normalizedParts.map((item) => stripBatchSeparatorArtifacts(restoreBatchSeparator(item)));
  }

  return null;
}

async function translateSingleEntry(entry, source, target, stats) {
  const translatedText = await requestTranslateText(entry.sourceText, source, target);
  stats.requestCount += 1;
  stats.batchCount += 1;
  return stripBatchSeparatorArtifacts(restoreBatchSeparator(translatedText));
}

async function translateBatch(batch, source, target, stats) {
  const sourceText = buildBatchPayload(batch);
  const translatedText = await requestTranslateText(sourceText, source, target);
  stats.requestCount += 1;
  stats.batchCount += 1;
  return parseBatchTranslatedText(translatedText, batch.length);
}

async function translateBatchWithRetry(batch, source, target, stats) {
  if (!Array.isArray(batch) || !batch.length) {
    return [];
  }

  if (batch.length === 1) {
    return [await translateSingleEntry(batch[0], source, target, stats)];
  }

  try {
    const translatedBatchTexts = await translateBatch(batch, source, target, stats);
    if (!translatedBatchTexts) {
      throw new Error('批量翻译结果解析失败');
    }
    return translatedBatchTexts;
  } catch (error) {
    const middleIndex = Math.ceil(batch.length / 2);
    const leftBatch = batch.slice(0, middleIndex);
    const rightBatch = batch.slice(middleIndex);
    const leftResult = await translateBatchWithRetry(leftBatch, source, target, stats);
    const rightResult = await translateBatchWithRetry(rightBatch, source, target, stats);
    return [...leftResult, ...rightResult];
  }
}

function fillTranslatedText(units, translatedMap) {
  const nodeSegmentMap = new Map();

  (units || []).forEach((item) => {
    const translatedText = translatedMap.get(item.unitId);
    if (typeof translatedText !== 'string') {
      return;
    }

    const currentSegments = nodeSegmentMap.get(item.nodeId) || [];
    currentSegments.push({
      segmentIndex: item.segmentIndex,
      translatedText: restoreInlineTokens(stripBatchSeparatorArtifacts(translatedText), item.protectedTokens),
      node: item.node,
      leadingWhitespace: item.leadingWhitespace,
      trailingWhitespace: item.trailingWhitespace,
    });
    nodeSegmentMap.set(item.nodeId, currentSegments);
  });

  nodeSegmentMap.forEach((segments) => {
    const firstSegment = segments[0];
    if (!firstSegment?.node) {
      return;
    }

    const mergedText = segments
      .slice()
      .sort((left, right) => left.segmentIndex - right.segmentIndex)
      .map((item) => item.translatedText || '')
      .join('');

    firstSegment.node.textContent = `${firstSegment.leadingWhitespace}${mergedText}${firstSegment.trailingWhitespace}`;
  });
}

async function translateTextUnits(units, source, target, maxConcurrentRequests = DEFAULT_MAX_CONCURRENT_REQUESTS) {
  const safeUnits = Array.isArray(units) ? units : [];
  if (!safeUnits.length) {
    return {
      translatedMap: new Map(),
      requestCount: 0,
      batchCount: 0,
      cacheHitCount: 0,
      uniqueTextCount: 0,
    };
  }

  const translatedMap = new Map();
  const cachedUnitIds = new Set();
  let cacheHitCount = 0;

  safeUnits.forEach((item) => {
    const cachedText = getTextTranslationCache({
      source,
      target,
      text: item.sourceText,
    });
    if (typeof cachedText === 'string') {
      translatedMap.set(item.unitId, cachedText);
      cachedUnitIds.add(item.unitId);
      cacheHitCount += 1;
    }
  });

  const uncachedUnits = safeUnits.filter((item) => !cachedUnitIds.has(item.unitId));
  const translateEntries = buildTranslateEntries(uncachedUnits);
  const batches = buildTranslateBatches(translateEntries);
  const stats = {
    requestCount: 0,
    batchCount: 0,
  };
  const workerCount = Math.min(Math.max(1, maxConcurrentRequests), batches.length || 1);
  let cursor = 0;

  async function worker() {
    while (cursor < batches.length) {
      const currentIndex = cursor;
      cursor += 1;
      const batch = batches[currentIndex];

      const translatedBatchTexts = await translateBatchWithRetry(batch, source, target, stats);
      batch.forEach((entry, entryIndex) => {
        const translatedText = translatedBatchTexts[entryIndex];
        if (typeof translatedText === 'string') {
          entry.unitIds.forEach((unitId) => {
            translatedMap.set(unitId, translatedText);
          });
          setTextTranslationCache({
            source,
            target,
            text: entry.sourceText,
            translatedText,
          });
        }
      });
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return {
    translatedMap,
    requestCount: stats.requestCount,
    batchCount: stats.batchCount,
    cacheHitCount,
    uniqueTextCount: translateEntries.length,
  };
}

export function extractMailVisibleText(html) {
  const parsed = parseMailHtml(html);
  const translatableNodes = collectTextNodes(parsed.root);

  return translatableNodes
    .map((item) => item.textToTranslate)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export async function translateMailHtmlContent({
  emailId,
  html,
  source = 'auto',
  target = 'en',
  maxConcurrentRequests = DEFAULT_MAX_CONCURRENT_REQUESTS,
  useCache = true,
} = {}) {
  const parsed = parseMailHtml(html);
  const translatedDigest = md5(parsed.normalizedHtml || '');

  if (useCache && emailId) {
    const cachedResult = getTranslationCache({
      emailId,
      language: target,
      digest: translatedDigest,
    });
    if (cachedResult?.translatedHtml) {
      return {
        translatedHtml: cachedResult.translatedHtml,
        translatedDigest,
        translatedCount: 0,
        requestCount: 0,
        batchCount: 0,
        cacheHitCount: 0,
        fromCache: true,
      };
    }
  }

  const mailTaskKey = `${source || 'auto'}__${target || 'en'}__${translatedDigest}`;
  if (inFlightMailTranslationTasks.has(mailTaskKey)) {
    return inFlightMailTranslationTasks.get(mailTaskKey);
  }

  const translateTask = (async () => {
    const translatableTextNodes = collectTextNodes(parsed.root);
    if (!translatableTextNodes.length) {
      return {
        translatedHtml: exportMailHtml(parsed),
        translatedDigest,
        translatedCount: 0,
        requestCount: 0,
        batchCount: 0,
        cacheHitCount: 0,
        fromCache: false,
      };
    }

    const translationUnits = buildTranslateUnits(translatableTextNodes);
    const {
      translatedMap,
      requestCount,
      batchCount,
      cacheHitCount,
      uniqueTextCount,
    } = await translateTextUnits(translationUnits, source, target, maxConcurrentRequests);

    fillTranslatedText(translationUnits, translatedMap);
    const translatedHtml = exportMailHtml(parsed);

    if (useCache && emailId && translatedHtml) {
      setTranslationCache({
        emailId,
        language: target,
        translatedHtml,
        translatedDigest,
      });
    }

    return {
      translatedHtml,
      translatedDigest,
      translatedCount: translatableTextNodes.length,
      requestCount,
      batchCount,
      cacheHitCount,
      uniqueTextCount,
      fromCache: false,
    };
  })();

  inFlightMailTranslationTasks.set(mailTaskKey, translateTask);
  try {
    return await translateTask;
  } finally {
    inFlightMailTranslationTasks.delete(mailTaskKey);
  }
}
